<template>
	<div class="designer">
		<!-- 顶部标题 + 工具条 -->
		<div class="d-header">
			<div class="d-title">
				<span class="back" @click="back">‹ 返回</span>
				<input class="rpt-name" v-model="form.reportName" placeholder="未命名报表" />
				<span class="period">
					期间：<input type="month" v-model="form.periodRange" />
				</span>
			</div>
			<div class="ribbon">
				<button class="ribbon-tab" :class="{active: !panelOpen}" @click="panelOpen=false">开始</button>
				<button class="ribbon-tab" :class="{active: panelOpen && panelTab==='formula'}" @click="openPanel('formula')">公式</button>
				<button class="ribbon-tab" :class="{active: panelOpen && panelTab==='data'}" @click="openPanel('data')">数据</button>
				<span class="zoom">100%</span>
			</div>
			<div class="toolbar">
				<button class="h-btn h-btn-primary" @click="save"><i class="h-icon-check"></i>保存</button>
				<button class="h-icon-close" @click="back">关闭</button>
				<button @click="copyReport">复制</button>
				<button @click="renameReport">重命名</button>
				<button @click="delReport" class="danger">删除</button>
				<span class="sep"></span>
				<button @click="changeColor">更改颜色</button>
				<button @click="hideCol">隐藏</button>
				<button @click="unhideAll">取消隐藏</button>
				<button @click="toggleProtect">{{ protectedSheet ? '取消保护工作表' : '保护工作表' }}</button>
				<button @click="toast('工作表权限演示')">更改工作表权限</button>
				<button @click="toast('保护范围演示')">查看所有保护范围</button>
			</div>
		</div>

		<!-- 主体：网格 + 侧栏 -->
		<div class="d-body">
			<div class="d-main">
				<!-- 公式栏 -->
				<div class="formula-bar">
					<span class="cell-ref">{{ selected ? cellKey(selected.r, selected.c) : '' }}</span>
					<input class="formula-input" v-model="formulaText" placeholder="输入数值或公式，如 =QM(&quot;1001&quot;)+QM(&quot;1002&quot;)"
						@keyup.enter="applyFormula" @focus="onFormulaFocus" />
				</div>
				<!-- 电子表格 -->
				<div class="grid-wrap" :class="{protected: protectedSheet}">
					<table class="grid">
						<thead>
							<tr>
								<th class="corner"></th>
								<th v-for="c in cols" :key="'h'+c" :class="{hidden: colHidden[colName(c)]}">{{ colName(c) }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="r in rows" :key="'r'+r" :class="{hidden: rowHidden[r]}">
								<th class="rownum">{{ r }}</th>
								<td v-for="c in cols" :key="cellKey(r-1,c-1)"
									:class="tdClass(r-1,c-1)"
									:style="cellStyle(r-1,c-1)">
									<input class="cell-input" :value="cellDisplay(r-1,c-1)"
										:readonly="protectedSheet"
										@focus="onCellFocus(r-1,c-1, $event)"
										@input="onCellInput(r-1,c-1, $event)"
										@blur="onCellBlur(r-1,c-1)" />
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="err-box" v-if="errors.length">
					<b>计算提示：</b>
					<span v-for="(e,i) in errors" :key="i" class="err">{{ e }}</span>
				</div>
			</div>

			<!-- 侧栏：参照 / 函数文档 -->
			<div class="d-side" v-if="panelOpen">
				<div class="side-tabs">
					<span :class="{active: panelTab==='formula'}" @click="panelTab='formula'">报表函数文档</span>
					<span :class="{active: panelTab==='data'}" @click="panelTab='data'">编码参照</span>
				</div>
				<div class="side-body">
					<!-- 公式文档 -->
					<div v-show="panelTab==='formula'" class="doc">
						<h4>取数函数</h4>
						<ul>
							<li><code>QM("1001")</code> 期末余额（别名 <code>ACCT</code>）</li>
							<li><code>QC("1001")</code> 期初余额</li>
							<li><code>FS("1001"[, "借"|"贷"])</code> 本期发生额</li>
							<li><code>LFS("1001"[, "借"|"贷"])</code> 本年累计发生额</li>
							<li><code>SUM(A1:B3)</code> 区间求和</li>
							<li><code>ABS(x)</code> 绝对值</li>
						</ul>
						<h4>运算符</h4>
						<p>+ - * / ( ) 以及单元格引用（如 A1、B2）。公式以 <code>=</code> 开头。</p>
						<h4>取数案例</h4>
						<pre>货币资金 = QM("1001")+QM("1002")+QM("1012")
应收账款 = QC("1122")
主营业务收入(本月) = FS("6001","贷")
本年利润(累计) = LFS("6001","贷")-LFS("6401","借")</pre>
					</div>
					<!-- 编码参照 -->
					<div v-show="panelTab==='data'">
						<div class="ref-block">
							<h4>科目编码参照</h4>
							<input class="ref-search" v-model="subjKw" placeholder="搜索科目编码/名称" @input="loadSubjects" />
							<div class="ref-list">
								<div class="ref-item" v-for="s in subjects" :key="s.code" @click="applyRefCode('QM', s.code)">
									<span class="code">{{ s.code }}</span><span class="nm">{{ s.name }}</span>
									<span class="dir">{{ s.balance_direction }}</span>
								</div>
							</div>
						</div>
						<div class="ref-block">
							<h4>辅助编码参照</h4>
							<div class="ref-list">
								<div v-for="cat in auxiliaries" :key="cat.id" class="aux-cat">
									<div class="aux-cat-name">{{ cat.name }}</div>
									<div class="ref-item" v-for="it in (cat.items||[])" :key="it.code"
										@click="applyRefCode('QM', it.subject_code || '')">
										<span class="code">{{ it.code }}</span><span class="nm">{{ it.name }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'BiReportDesigner',
		props: {
			reportId: { type: [String, Number], default: 0 }
		},
		data() {
			return {
				form: { id: 0, reportName: '', groupName: '', displayPosition: 0, dataDimension: '科目', periodRange: '', remark: '' },
				rows: 30,
				cols: 12,
				cells: {},
				colHidden: {},
				rowHidden: {},
				protectedSheet: false,
				selected: null,
				editing: null,
				formulaText: '',
				panelOpen: false,
				panelTab: 'formula',
				subjKw: '',
				subjects: [],
				auxiliaries: [],
				errors: []
			};
		},
		methods: {
			colName(c) { return String.fromCharCode(65 + c); },
			cellKey(r, c) { return this.colName(c) + (r + 1); },
			curMonth() {
				const d = new Date();
				return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
			},
			// ---------- 单元格显示 ----------
			cellDisplay(r, c) {
				const key = this.cellKey(r, c);
				const cell = this.cells[key] || {};
				if (this.editing === key) return cell.f || '';
				if (cell.f && cell.f.startsWith('=')) return this.fmt(cell.v);
				return cell.f || '';
			},
			fmt(v) {
				if (v === null || v === undefined || v === '') return '';
				if (typeof v === 'number' && isFinite(v)) {
					return v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
				}
				return v;
			},
			tdClass(r, c) {
				const key = this.cellKey(r, c);
				return { selected: this.selected && this.selected.r === r && this.selected.c === c };
			},
			cellStyle(r, c) {
				const key = this.cellKey(r, c);
				const cell = this.cells[key] || {};
				const s = cell.style || {};
				return { background: s.bg || '', color: s.color || '', fontWeight: s.bold ? 'bold' : '' };
			},
			// ---------- 编辑 ----------
			onCellFocus(r, c, ev) {
				this.selected = { r, c };
				this.editing = this.cellKey(r, c);
				this.formulaText = (this.cells[this.cellKey(r, c)] || {}).f || '';
				if (this.protectedSheet) return;
			},
			onCellInput(r, c, ev) {
				if (this.protectedSheet) return;
				const key = this.cellKey(r, c);
				this.cells[key] = Object.assign({}, this.cells[key], { f: ev.target.value });
			},
			onCellBlur(r, c) {
				this.editing = null;
				this.recompute();
			},
			onFormulaFocus() { this.panelOpen = true; this.panelTab = 'formula'; },
			applyFormula() {
				if (!this.selected) { this.$Message.warning('请先选中一个单元格'); return; }
				const key = this.cellKey(this.selected.r, this.selected.c);
				this.cells[key] = Object.assign({}, this.cells[key], { f: this.formulaText });
				this.recompute();
			},
			// ---------- 计算 ----------
			buildContent() {
				return {
					cols: this.cols,
					rows: this.rows,
					cells: this.cells,
					colHidden: this.colHidden,
					rowHidden: this.rowHidden,
					protected: this.protectedSheet
				};
			},
			parseContent(str) {
				if (!str) return;
				try {
					const o = JSON.parse(str);
					if (o.cols) this.cols = o.cols;
					if (o.rows) this.rows = o.rows;
					if (o.cells) this.cells = o.cells;
					if (o.colHidden) this.colHidden = o.colHidden;
					if (o.rowHidden) this.rowHidden = o.rowHidden;
					if (o.protected) this.protectedSheet = !!o.protected;
				} catch (e) { /* 忽略损坏内容 */ }
			},
			recompute() {
				const content = JSON.stringify(this.buildContent());
				this.$api.bi.compute({ content, period: this.form.periodRange || this.curMonth() }).then(({ data }) => {
					const values = (data && data.values) || {};
					const nv = {};
					for (const k in this.cells) {
						nv[k] = Object.assign({}, this.cells[k], { v: values[k] !== undefined ? values[k] : this.cells[k].v });
					}
					this.cells = nv;
					this.errors = (data && data.errors) || [];
				}).catch(() => {});
			},
			// ---------- 参照 ----------
			openPanel(tab) { this.panelOpen = true; this.panelTab = tab; if (this.panelTab === 'data') this.loadSubjects(); },
			loadSubjects() {
				this.$api.bi.subjects({ keyword: this.subjKw }).then(({ data }) => { this.subjects = data || []; });
			},
			loadAux() {
				this.$api.bi.auxiliaries().then(({ data }) => { this.auxiliaries = data || []; });
			},
			applyRef(text) {
				if (this.selected) {
					const key = this.cellKey(this.selected.r, this.selected.c);
					this.cells[key] = Object.assign({}, this.cells[key], { f: text });
					this.formulaText = text;
					this.recompute();
				} else {
					this.formulaText = this.formulaText ? this.formulaText + text : text;
				}
			},
			// 拼接取数函数文本。注意：模板属性里不能出现 \" （HTML 无法转义引号，会截断属性并破坏编译），
			// 因此引号只在 JS 中拼接，模板只传函数与编码。
			applyRefCode(fn, code) {
				if (!code) return;
				this.applyRef(fn + '("' + code + '")');
			},
			// ---------- 工具栏 ----------
			changeColor() {
				if (!this.selected) { this.$Message.warning('请选中单元格'); return; }
				const color = window.prompt('输入背景色（如 #FFF2CC 或 red）', '#FFF2CC');
				if (!color) return;
				const key = this.cellKey(this.selected.r, this.selected.c);
				const cell = Object.assign({}, this.cells[key]);
				cell.style = Object.assign({}, cell.style, { bg: color });
				this.cells[key] = cell;
				this.recompute();
			},
			hideCol() {
				if (!this.selected) { this.$Message.warning('请选中单元格'); return; }
				this.$set(this.colHidden, this.colName(this.selected.c), true);
			},
			unhideAll() { this.colHidden = {}; this.rowHidden = {}; },
			toggleProtect() { this.protectedSheet = !this.protectedSheet; this.recompute(); },
			toast(msg) { this.$Message.info(msg); },
			// ---------- 持久化 ----------
			save() {
				if (!this.form.reportName) { this.$Message.warning('请填写报表名称'); return; }
				const entity = {
					id: this.form.id || undefined,
					reportName: this.form.reportName,
					groupName: this.form.groupName,
					displayPosition: this.form.displayPosition || 0,
					dataDimension: this.form.dataDimension,
					periodRange: this.form.periodRange,
					remark: this.form.remark,
					content: JSON.stringify(this.buildContent())
				};
				const req = this.form.id ? this.$api.bi.update(entity) : this.$api.bi.save(entity);
				req.then(({ data }) => {
					if (!this.form.id && data) this.form.id = data;
					this.$Message.success('已保存');
					this.recompute();
				});
			},
			copyReport() {
				if (!this.form.id) { this.$Message.warning('请先保存当前报表'); return; }
				const name = window.prompt('新报表名称', this.form.reportName + '_副本');
				if (!name) return;
				this.$api.bi.copy({ id: this.form.id, name }).then(({ data }) => {
					this.$Message.success('已复制');
					this.$router.replace({ name: 'BiReportDesigner', params: { reportId: data } });
				});
			},
			renameReport() {
				if (!this.form.id) { this.$Message.warning('请先保存当前报表'); return; }
				const name = window.prompt('新报表名称', this.form.reportName);
				if (!name) return;
				this.$api.bi.rename({ id: this.form.id, name }).then(() => {
					this.form.reportName = name;
					this.$Message.success('已重命名');
				});
			},
			delReport() {
				if (!this.form.id) { this.back(); return; }
				this.$Confirm('确认删除该报表？').then(() => {
					this.$api.bi.delete(this.form.id).then(() => { this.$Message.success('已删除'); this.back(); });
				});
			},
			back() { this.$router.push({ name: 'BiReportList' }); },
			// ---------- 加载 ----------
			loadReport() {
				const id = parseInt(this.reportId, 10);
				if (!id || id <= 0) {
					this.form.id = 0;
					this.form.periodRange = this.curMonth();
					this.recompute();
					return;
				}
				this.$api.bi.load(id).then(({ data }) => {
					if (!data) return;
					this.form = {
						id: data.id,
						reportName: data.reportName,
						groupName: data.groupName,
						displayPosition: data.displayPosition,
						dataDimension: data.dataDimension || '科目',
						periodRange: data.periodRange || this.curMonth(),
						remark: data.remark
					};
					this.parseContent(data.content);
					this.recompute();
				});
			}
		},
		mounted() {
			this.loadReport();
			this.loadAux();
		}
	};
</script>

<style scoped lang="less">
	.designer { height: 100%; display: flex; flex-direction: column; background: #fff; }
	.d-header { border-bottom: 1px solid #e8e8e8; }
	.d-title { display: flex; align-items: center; padding: 8px 14px; gap: 12px; }
	.back { color: @primary-color; cursor: pointer; font-size: 14px; }
	.rpt-name { font-size: 16px; font-weight: 600; border: none; border-bottom: 1px solid #ddd; width: 320px; padding: 2px 4px; }
	.period { color: #666; font-size: 13px; input { margin-left: 4px; } }
	.ribbon { display: flex; align-items: center; padding: 0 14px; background: #f5f5f5; border-top: 1px solid #eee; }
	.ribbon-tab { background: transparent; border: none; padding: 8px 18px; cursor: pointer; font-size: 13px; border-bottom: 2px solid transparent; }
	.ribbon-tab.active { border-bottom-color: @primary-color; font-weight: 600; }
	.zoom { margin-left: auto; color: #999; font-size: 12px; padding-right: 10px; }
	.toolbar { display: flex; align-items: center; gap: 6px; padding: 8px 14px; flex-wrap: wrap; }
	.toolbar button { padding: 5px 10px; border: 1px solid #d9d9d9; background: #fff; border-radius: 3px; cursor: pointer; font-size: 13px; }
	.toolbar button:hover { border-color: @primary-color; color: @primary-color; }
	.toolbar button.danger:hover { border-color: #e74c3c; color: #e74c3c; }
	.toolbar .sep { width: 1px; height: 20px; background: #ddd; margin: 0 4px; }
	.h-icon-close { border: none; background: none; cursor: pointer; color: #666; }

	.d-body { flex: 1; display: flex; overflow: hidden; }
	.d-main { flex: 1; display: flex; flex-direction: column; overflow: auto; padding: 10px 14px; }
	.formula-bar { display: flex; align-items: center; border: 1px solid #d9d9d9; border-radius: 3px; margin-bottom: 8px; }
	.cell-ref { width: 56px; text-align: center; border-right: 1px solid #eee; padding: 6px 0; font-size: 12px; color: #666; background: #fafafa; }
	.formula-input { flex: 1; border: none; padding: 6px 10px; font-size: 13px; outline: none; }
	.grid-wrap { overflow: auto; border: 1px solid #d9d9d9; max-height: 100%; }
	.grid { border-collapse: collapse; width: 100%; }
	.grid th, .grid td { border: 1px solid #e8e8e8; }
	.grid thead th, .grid .rownum { background: #f5f5f5; text-align: center; font-weight: 600; color: #666; font-size: 12px; }
	.grid thead th { min-width: 90px; padding: 4px 6px; }
	.grid .corner { width: 40px; }
	.grid .rownum { width: 40px; }
	.grid td { padding: 0; }
	.grid td.selected { outline: 2px solid @primary-color; outline-offset: -2px; }
	.grid td.hidden, .grid th.hidden, .grid tr.hidden { display: none; }
	.cell-input { width: 100%; box-sizing: border-box; border: none; padding: 5px 6px; font-size: 13px; outline: none; background: transparent; text-align: right; }
	.grid-wrap.protected .cell-input { background: #fafafa; color: #333; }
	.err-box { margin-top: 8px; font-size: 12px; color: #e67e22; .err { display: inline-block; margin-right: 12px; } }

	.d-side { width: 320px; border-left: 1px solid #e8e8e8; display: flex; flex-direction: column; background: #fafafa; }
	.side-tabs { display: flex; border-bottom: 1px solid #e8e8e8; }
	.side-tabs span { padding: 8px 14px; cursor: pointer; font-size: 13px; }
	.side-tabs span.active { font-weight: 600; color: @primary-color; border-bottom: 2px solid @primary-color; }
	.side-body { flex: 1; overflow: auto; padding: 10px 12px; font-size: 13px; }
	.doc h4 { margin: 12px 0 4px; font-size: 13px; }
	.doc ul { margin: 0; padding-left: 18px; }
	.doc li { margin: 3px 0; }
	.doc code { background: #eee; padding: 1px 4px; border-radius: 3px; }
	.doc pre { background: #2d2d2d; color: #eee; padding: 8px; border-radius: 4px; font-size: 12px; overflow: auto; }
	.ref-block { margin-bottom: 14px; }
	.ref-block h4 { margin: 6px 0; }
	.ref-search { width: 100%; padding: 5px 8px; border: 1px solid #d9d9d9; border-radius: 3px; margin-bottom: 6px; }
	.ref-list { max-height: 240px; overflow: auto; border: 1px solid #eee; background: #fff; }
	.ref-item { display: flex; gap: 8px; padding: 5px 8px; cursor: pointer; border-bottom: 1px solid #f3f3f3; }
	.ref-item:hover { background: #eef6ff; }
	.ref-item .code { color: #c0392b; font-family: monospace; width: 70px; }
	.ref-item .nm { flex: 1; }
	.ref-item .dir { color: #999; font-size: 12px; }
	.aux-cat-name { font-weight: 600; padding: 4px 8px; background: #f0f0f0; }
</style>
